// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  verify2FA: (data) => api.post('/auth/verify-2fa', data),
  resend2FA: (email) => api.post('/auth/resend-2fa', { email }),
};

// Workspace API
export const workspaceAPI = {
  list: () => api.get('/workspaces'),
  get: (id) => api.get(`/workspaces/${id}`),
  create: (data) => api.post('/workspaces', data),
  update: (id, data) => api.put(`/workspaces/${id}`, data),
  delete: (id) => api.delete(`/workspaces/${id}`),
  toggleArchive: (id) => api.patch(`/workspaces/${id}/archive`),
  getStats: (id) => api.get(`/workspaces/${id}/stats`),
  addParticipant: (workspaceId, userId) => api.post(`/workspaces/${workspaceId}/participants/${userId}`),
  removeParticipant: (workspaceId, userId) => api.delete(`/workspaces/${workspaceId}/participants/${userId}`),
  listParticipants: (workspaceId) => api.get(`/workspaces/${workspaceId}/participants`),
};

// Course API
export const courseAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/courses`),
  get: (workspaceId, courseId) => api.get(`/workspaces/${workspaceId}/courses/${courseId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/courses`, data),
  update: (workspaceId, courseId, data) => api.put(`/workspaces/${workspaceId}/courses/${courseId}`, data),
  delete: (workspaceId, courseId) => api.delete(`/workspaces/${workspaceId}/courses/${courseId}`),
  toggleVisibility: (workspaceId, courseId) => api.patch(`/workspaces/${workspaceId}/courses/${courseId}/visibility`),
  updateColor: (workspaceId, courseId, color) => api.patch(`/workspaces/${workspaceId}/courses/${courseId}/color`, { color }),
  toggleSectionVisibility: (workspaceId, courseId, sectionId) => api.patch(`/workspaces/${workspaceId}/courses/${courseId}/sections/${sectionId}/visibility`),
  getTree: (workspaceId) => api.get(`/workspaces/${workspaceId}/course-tree`),
  reorder: (workspaceId, courseIds) => api.patch(`/workspaces/${workspaceId}/courses/reorder`, { course_ids: courseIds }),
};

// TP (Travaux Pratiques) API
export const tpAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/tp`),
  get: (workspaceId, tpId) => api.get(`/workspaces/${workspaceId}/tp/${tpId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/tp`, data),
  update: (workspaceId, tpId, data) => api.put(`/workspaces/${workspaceId}/tp/${tpId}`, data),
  delete: (workspaceId, tpId) => api.delete(`/workspaces/${workspaceId}/tp/${tpId}`),
  toggleVisibility: (workspaceId, tpId) => api.patch(`/workspaces/${workspaceId}/tp/${tpId}/visibility`),
};

// URL Resource API
export const urlResourceAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/url-resources`),
  get: (workspaceId, resourceId) => api.get(`/workspaces/${workspaceId}/url-resources/${resourceId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/url-resources`, data),
  update: (workspaceId, resourceId, data) => api.put(`/workspaces/${workspaceId}/url-resources/${resourceId}`, data),
  delete: (workspaceId, resourceId) => api.delete(`/workspaces/${workspaceId}/url-resources/${resourceId}`),
  toggleVisibility: (workspaceId, resourceId) => api.patch(`/workspaces/${workspaceId}/url-resources/${resourceId}/visibility`),
};

// Quiz API
export const quizAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/quizzes`),
  get: (workspaceId, quizId) => api.get(`/workspaces/${workspaceId}/quizzes/${quizId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/quizzes`, data),
  update: (workspaceId, quizId, data) => api.put(`/workspaces/${workspaceId}/quizzes/${quizId}`, data),
  delete: (workspaceId, quizId) => api.delete(`/workspaces/${workspaceId}/quizzes/${quizId}`),
  toggleVisibility: (workspaceId, quizId) => api.patch(`/workspaces/${workspaceId}/quizzes/${quizId}/visibility`),
  submit: (workspaceId, quizId, data) => api.post(`/workspaces/${workspaceId}/quizzes/${quizId}/submit`, data),
  getAttempts: (workspaceId, quizId) => api.get(`/workspaces/${workspaceId}/quizzes/${quizId}/attempts`),
  getMyAttempts: (workspaceId, quizId) => api.get(`/workspaces/${workspaceId}/quizzes/${quizId}/my-attempts`),
  getAttemptDetail: (workspaceId, quizId, attemptId) => api.get(`/workspaces/${workspaceId}/quizzes/${quizId}/attempts/${attemptId}`),
  deleteAttempt: (workspaceId, quizId, attemptId) => api.delete(`/workspaces/${workspaceId}/quizzes/${quizId}/attempts/${attemptId}`),
};

// Assignment API
export const assignmentAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/assignments`),
  get: (workspaceId, assignmentId) => api.get(`/workspaces/${workspaceId}/assignments/${assignmentId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/assignments`, data),
  update: (workspaceId, assignmentId, data) => api.put(`/workspaces/${workspaceId}/assignments/${assignmentId}`, data),
  delete: (workspaceId, assignmentId) => api.delete(`/workspaces/${workspaceId}/assignments/${assignmentId}`),
  toggleVisibility: (workspaceId, assignmentId) => api.patch(`/workspaces/${workspaceId}/assignments/${assignmentId}/visibility`),
  toggleSubmission: (workspaceId, assignmentId) => api.patch(`/workspaces/${workspaceId}/assignments/${assignmentId}/submission-toggle`),
};

// Submission API
export const submissionAPI = {
  list: (assignmentId) => api.get(`/assignments/${assignmentId}/submissions`),
  get: (assignmentId, submissionId) => api.get(`/assignments/${assignmentId}/submissions/${submissionId}`),
  submit: (assignmentId, formData) => api.post(`/assignments/${assignmentId}/submissions`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  grade: (assignmentId, submissionId, data) => api.put(`/assignments/${assignmentId}/submissions/${submissionId}/grade`, data),
  delete: (assignmentId, submissionId) => api.delete(`/assignments/${assignmentId}/submissions/${submissionId}`),
  getStats: (workspaceId, assignmentId) => api.get(`/workspaces/${workspaceId}/assignments/${assignmentId}/stats`),
  aiAnalyze: (assignmentId, submissionId) => api.post(`/assignments/${assignmentId}/submissions/${submissionId}/ai-analyze`),
};

// Meeting API
export const meetingAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/meetings`),
  get: (workspaceId, meetingId) => api.get(`/workspaces/${workspaceId}/meetings/${meetingId}`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/meetings`, data),
  update: (workspaceId, meetingId, data) => api.put(`/workspaces/${workspaceId}/meetings/${meetingId}`, data),
  delete: (workspaceId, meetingId) => api.delete(`/workspaces/${workspaceId}/meetings/${meetingId}`),
  toggleActive: (workspaceId, meetingId) => api.patch(`/workspaces/${workspaceId}/meetings/${meetingId}/toggle-active`),
};

// Message API (Chat)
export const messageAPI = {
  list: (workspaceId, since = null) => api.get(`/workspaces/${workspaceId}/messages`, { params: { since } }),
  send: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/messages`, data),
  delete: (workspaceId, messageId) => api.delete(`/workspaces/${workspaceId}/messages/${messageId}`),
};

// File API
export const fileAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/files`),
  upload: (workspaceId, formData) => api.post(`/workspaces/${workspaceId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (workspaceId, fileId) => api.delete(`/workspaces/${workspaceId}/files/${fileId}`),
  getDownloadUrl: (filename) => `${API_BASE}/files/download/${filename}`,
};

// Staff Chat API
export const staffChatAPI = {
  listConversations: () => api.get('/staff-chat/conversations'),
  createConversation: (data) => api.post('/staff-chat/conversations', data),
  listMessages: (conversationId, since = null) => api.get(`/staff-chat/conversations/${conversationId}/messages`, { params: { since } }),
  sendMessage: (conversationId, data) => api.post(`/staff-chat/conversations/${conversationId}/messages`, data),
  deleteConversation: (conversationId) => api.delete(`/staff-chat/conversations/${conversationId}`),
  listStaffUsers: () => api.get('/staff-chat/staff-users'),
};

// Direct Chat API (formateur <-> candidat)
export const directChatAPI = {
  listConversations: () => api.get("/direct-chat/conversations"),
  createConversation: (targetUserId) => api.post("/direct-chat/conversations", { target_user_id: targetUserId }),
  listMessages: (conversationId) => api.get("/direct-chat/conversations/" + conversationId + "/messages"),
  sendMessage: (conversationId, content) => api.post("/direct-chat/conversations/" + conversationId + "/messages", { content }),
  markAsRead: (conversationId) => api.put("/direct-chat/conversations/" + conversationId + "/read"),
  getUnreadCount: () => api.get("/direct-chat/unread-count"),
};

// Admin API
export const adminAPI = {
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  listLockedUsers: () => api.get('/admin/users/locked'),
  unlockUser: (userId) => api.post(`/admin/users/${userId}/unlock`),
  listPendingUsers: () => api.get('/admin/users/pending'),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  approveUser: (userId) => api.post(`/admin/users/${userId}/approve`),
  rejectUser: (userId, reason) => api.post(`/admin/users/${userId}/reject`, { reason }),
  setUserExpiration: (userId, expiresAt) => api.post(`/admin/users/${userId}/set-expiration`, { expires_at: expiresAt }),
  getStats: () => api.get('/admin/stats'),
  getActivities: (limit = 50) => api.get(`/admin/activities?limit=${limit}`),
  getConfig: (key) => api.get(`/admin/config/${key}`),
  updateConfig: (key, data) => api.put(`/admin/config/${key}`, data),
  updateSMTPConfig: (data) => api.put('/admin/config/smtp', data),
  updateJitsiConfig: (data) => api.put('/admin/config/jitsi', data),
  updateS3Config: (data) => api.put('/admin/config/s3', data),
  getAutoApprove: () => api.get('/admin/config/auto-approve'),
  setAutoApprove: (enabled) => api.put(`/admin/config/auto-approve?enabled=${enabled}`),
  testEmail: (toEmail) => api.post(`/admin/test-email?to_email=${encodeURIComponent(toEmail)}`),
  getEmailDomains: () => api.get('/admin/config/allowed-email-domains'),
  updateEmailDomains: (domains) => api.put('/admin/config/allowed-email-domains', { domains }),
  resetEmailDomains: () => api.delete('/admin/config/allowed-email-domains'),
  getEmailTemplates: () => api.get('/admin/email-templates'),
  updateEmailTemplates: (data) => api.put('/admin/email-templates', data),
  // Permissions
  listPermissions: () => api.get('/admin/permissions'),
  updatePermissions: (userId, data) => api.put(`/admin/permissions/${userId}`, data),
  // Mon Dossier visibility
  getMonDossierEnabled: () => api.get('/admin/config/mon-dossier-enabled'),
  setMonDossierEnabled: (enabled) => api.put(`/admin/config/mon-dossier-enabled?enabled=${enabled}`),
  // Feature flags
  getFeatureFlags: () => api.get('/config/features'),
};

// Category API
export const categoryAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/categories`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/categories`, data),
  update: (workspaceId, categoryId, data) => api.put(`/workspaces/${workspaceId}/categories/${categoryId}`, data),
  delete: (workspaceId, categoryId) => api.delete(`/workspaces/${workspaceId}/categories/${categoryId}`),
};

// Backup & Restore API
export const backupAPI = {
  getPolicy: () => api.get('/admin/backup/policies'),
  updatePolicy: (data) => api.put('/admin/backup/policies', data),
  runBackup: (data) => api.post('/admin/backup/run', data),
  listJobs: (params) => api.get('/admin/backup/jobs', { params }),
  getJob: (jobId) => api.get(`/admin/backup/jobs/${jobId}`),
  deleteJob: (jobId) => api.delete(`/admin/backup/jobs/${jobId}`),
  downloadUrl: (jobId) => `${API_BASE}/admin/backup/jobs/${jobId}/download`,
  restore: (data) => api.post('/admin/backup/restore', data),
  getRestoreStatus: (limit) => api.get('/admin/backup/restore/status', { params: { limit } }),
  getStats: () => api.get('/admin/backup/stats'),
  // Per-workspace
  // Workspace Export / Import (new ZIP-based)
  exportWorkspace: (wid) => api.post(`/admin/backup/workspace/${wid}/export`),
  downloadWorkspaceUrl: (wid) => `${API_BASE}/admin/backup/workspace/${wid}/download`,
  importWorkspace: (backupId) => api.post(`/admin/backup/workspace/import/${backupId}`),
  listWorkspaceExports: () => api.get('/admin/backup/workspace-exports'),
  listAllRestores: (limit = 20) => api.get('/admin/backup/restores', { params: { limit } }),
  uploadImportWorkspace: (formData) => api.post('/admin/backup/workspace/upload-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  workspaceBackup: (wid) => api.post(`/workspaces/${wid}/backup`),
  workspaceRestore: (wid, data) => api.post(`/workspaces/${wid}/restore`, data),
  workspaceBackupList: (wid) => api.get(`/workspaces/${wid}/backup/list`),
};

// Group API
export const groupAPI = {
  list: () => api.get('/groups'),
  create: (data) => api.post('/groups', data),
  update: (groupId, data) => api.put(`/groups/${groupId}`, data),
  delete: (groupId) => api.delete(`/groups/${groupId}`),
  addMember: (groupId, userId) => api.post(`/groups/${groupId}/members/${userId}`),
  removeMember: (groupId, userId) => api.delete(`/groups/${groupId}/members/${userId}`),
  listMembers: (groupId) => api.get(`/groups/${groupId}/members`),
};

// Notification API
export const notificationAPI = {
  list: (limit = 20) => api.get(`/notifications?limit=${limit}`),
  getCount: () => api.get('/notifications/count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Contact API
export const contactAPI = {
  create: (data) => api.post('/contacts', data),
  listMy: () => api.get('/contacts/my'),
  listAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    if (params.severity) query.append('severity', params.severity);
    if (params.assigned_to) query.append('assigned_to', params.assigned_to);
    if (params.group_id) query.append('group_id', params.group_id);
    const qs = query.toString();
    return api.get(`/contacts${qs ? '?' + qs : ''}`);
  },
  get: (contactId) => api.get(`/contacts/${contactId}`),
  updateStatus: (contactId, status) => api.put(`/contacts/${contactId}/status?status=${status}`),
  assign: (contactId, userId) => api.put(`/contacts/${contactId}/assign?user_id=${userId}`),
  delete: (contactId) => api.delete(`/contacts/${contactId}`),
  reply: (contactId, replyMessage, attachments = []) => api.post(`/contacts/${contactId}/reply`, { reply_message: replyMessage, attachments }),
  getBadgeCounts: (workspaceId) => api.get('/notifications/badge-counts' + (workspaceId ? '?workspace_id=' + workspaceId : '')),
};

// Add convenience methods to default export
api.getNotifications = (limit) => notificationAPI.list(limit);
api.getNotificationCount = () => notificationAPI.getCount();
api.markNotificationRead = (id) => notificationAPI.markAsRead(id);
api.markAllNotificationsRead = () => notificationAPI.markAllRead();
api.deleteNotification = (id) => notificationAPI.delete(id);

// Evaluations API
export const evaluationsAPI = {
  getAll: (params) => api.get('/evaluations', { params }),
  create: (data) => api.post('/evaluations', data),
  update: (id, data) => api.put(`/evaluations/${id}`, data),
  delete: (id) => api.delete(`/evaluations/${id}`),
  getGrades: (id) => api.get(`/evaluations/${id}/grades`),
  getAllGradesSummary: (workspaceId) => api.get('/all-grades-summary', { params: { workspace_id: workspaceId } }),
};

// Grades API
export const gradesAPI = {
  create: (data) => api.post('/grades', data),
  bulkCreate: (evaluationId, grades) => api.post(`/evaluations/${evaluationId}/grades/bulk`, grades),
  getStudentGrades: (studentId) => api.get(`/students/${studentId}/grades`)
};


// Remarks API
export const remarksAPI = {
  list: (workspaceId, params = {}) => api.get("/remarks", { params: { workspace_id: workspaceId, ...params } }),
  create: (data) => api.post("/remarks", data),
  update: (id, data) => api.put(`/remarks/${id}`, data),
  delete: (id) => api.delete(`/remarks/${id}`),
};
export const exportAPI = {
  attendance: (format, scheduleId) => api.get('/export/attendance', { params: { format, schedule_id: scheduleId }, responseType: 'blob' })
};
export const schedulesAPI = {
  getAll: (workspaceId) => api.get(`/planning/workspaces/${workspaceId}/schedules`),
};

// Temporary Public Sessions API
export const tempSessionAPI = {
  // Admin CRUD
  list: (workspaceId) => api.get('/temp-sessions/', { params: { workspace_id: workspaceId } }),
  get: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}`, { params: { workspace_id: workspaceId } }),
  create: (workspaceId, data, userId) => api.post('/temp-sessions/', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  update: (workspaceId, sessionId, data) => api.put(`/temp-sessions/${sessionId}`, data, { params: { workspace_id: workspaceId } }),
  delete: (workspaceId, sessionId) => api.delete(`/temp-sessions/${sessionId}`, { params: { workspace_id: workspaceId } }),
  publish: (workspaceId, sessionId) => api.post(`/temp-sessions/${sessionId}/publish`, null, { params: { workspace_id: workspaceId } }),
  regenerateLink: (workspaceId, sessionId) => api.post(`/temp-sessions/${sessionId}/regenerate-link`, null, { params: { workspace_id: workspaceId } }),
  duplicate: (workspaceId, sessionId, userId, targetWorkspaceId) => api.post(`/temp-sessions/${sessionId}/duplicate`, null, { params: { workspace_id: workspaceId, user_id: userId, ...(targetWorkspaceId ? { target_workspace_id: targetWorkspaceId } : {}) } }),
  // Items
  addItem: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/items`, data, { params: { workspace_id: workspaceId } }),
  removeItem: (workspaceId, sessionId, itemId) => api.delete(`/temp-sessions/${sessionId}/items/${itemId}`, { params: { workspace_id: workspaceId } }),
  updateItem: (workspaceId, sessionId, itemId, data) => api.patch(`/temp-sessions/${sessionId}/items/${itemId}`, data, { params: { workspace_id: workspaceId } }),
  reorderItems: (workspaceId, sessionId, items) => api.put(`/temp-sessions/${sessionId}/items/reorder`, items, { params: { workspace_id: workspaceId } }),
  // Logs
  getLogs: (workspaceId, sessionId, params = {}) => api.get(`/temp-sessions/${sessionId}/logs`, { params: { workspace_id: workspaceId, ...params } }),
  // Attendance (admin)
  getAttendance: (workspaceId, sessionId, params = {}) => api.get(`/temp-sessions/${sessionId}/attendance`, { params: { workspace_id: workspaceId, ...params } }),
  exportAttendance: (workspaceId, sessionId, date = null) => api.get(`/temp-sessions/${sessionId}/attendance/export`, { params: { workspace_id: workspaceId, ...(date ? { date } : {}) }, responseType: 'blob' }),
  exportAttendancePDF: (workspaceId, sessionId, date = null) => api.get(`/temp-sessions/${sessionId}/attendance/export-pdf`, { params: { workspace_id: workspaceId, ...(date ? { date } : {}) }, responseType: 'blob' }),
  addManualAttendance: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/attendance/manual`, data, { params: { workspace_id: workspaceId } }),
  deleteAttendance: (workspaceId, sessionId, recordId) => api.delete(`/temp-sessions/${sessionId}/attendance/${recordId}`, { params: { workspace_id: workspaceId } }),
  // Files
  uploadFile: (workspaceId, sessionId, formData) => api.post(`/temp-sessions/${sessionId}/files`, formData, { params: { workspace_id: workspaceId }, headers: { 'Content-Type': 'multipart/form-data' } }),
  listFiles: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/files`, { params: { workspace_id: workspaceId } }),
  deleteFile: (workspaceId, sessionId, fileId) => api.delete(`/temp-sessions/${sessionId}/files/${fileId}`, { params: { workspace_id: workspaceId } }),
  updateFile: (workspaceId, sessionId, fileId, data) => api.patch(`/temp-sessions/${sessionId}/files/${fileId}`, data, { params: { workspace_id: workspaceId } }),
  // Spreadsheet
  getSpreadsheet: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/spreadsheet`, { params: { workspace_id: workspaceId } }),
  saveSpreadsheet: (workspaceId, sessionId, data) => api.put(`/temp-sessions/${sessionId}/spreadsheet`, data, { params: { workspace_id: workspaceId } }),
  sendSessionLink: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/send-session-link`, data, { params: { workspace_id: workspaceId } }),
  // Shared Links
  listLinks: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/links`, { params: { workspace_id: workspaceId } }),
  createLink: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/links`, data, { params: { workspace_id: workspaceId } }),
  deleteLink: (workspaceId, sessionId, linkId) => api.delete(`/temp-sessions/${sessionId}/links/${linkId}`, { params: { workspace_id: workspaceId } }),
  updateLink: (workspaceId, sessionId, linkId, data) => api.patch(`/temp-sessions/${sessionId}/links/${linkId}`, data, { params: { workspace_id: workspaceId } }),
  // Program
  getProgram: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/program`, { params: { workspace_id: workspaceId } }),
  saveProgram: (workspaceId, sessionId, data) => api.put(`/temp-sessions/${sessionId}/program`, data, { params: { workspace_id: workspaceId } }),
  // Contact requests
  getContacts: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/contacts`, { params: { workspace_id: workspaceId } }),
  markContactRead: (workspaceId, sessionId, contactId) => api.patch(`/temp-sessions/${sessionId}/contacts/${contactId}/read`, null, { params: { workspace_id: workspaceId } }),
  deleteContact: (workspaceId, sessionId, contactId) => api.delete(`/temp-sessions/${sessionId}/contacts/${contactId}`, { params: { workspace_id: workspaceId } }),
  // Shortlist (Candidats Retenus)
  getShortlist: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/shortlist`, { params: { workspace_id: workspaceId } }),
  addToShortlist: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/shortlist`, data, { params: { workspace_id: workspaceId } }),
  updateShortlist: (workspaceId, sessionId, candidateId, data) => api.patch(`/temp-sessions/${sessionId}/shortlist/${candidateId}`, data, { params: { workspace_id: workspaceId } }),
  removeFromShortlist: (workspaceId, sessionId, candidateId) => api.delete(`/temp-sessions/${sessionId}/shortlist/${candidateId}`, { params: { workspace_id: workspaceId } }),
  // Shortlist Email
  getEmailTemplate: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/shortlist-email/template`, { params: { workspace_id: workspaceId } }),
  saveEmailTemplate: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/shortlist-email/template`, data, { params: { workspace_id: workspaceId } }),
  previewEmail: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/shortlist-email/preview`, data, { params: { workspace_id: workspaceId } }),
  sendShortlistEmails: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/shortlist-email/send`, data, { params: { workspace_id: workspaceId } }),
  sendOneEmail: (workspaceId, sessionId, candidateId, data) => api.post(`/temp-sessions/${sessionId}/shortlist-email/send-one/${candidateId}`, data, { params: { workspace_id: workspaceId } }),
  // Rejection Email
  getRejectionTemplate: (workspaceId, sessionId) => api.get(`/temp-sessions/${sessionId}/rejection-email/template`, { params: { workspace_id: workspaceId } }),
  saveRejectionTemplate: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/rejection-email/template`, data, { params: { workspace_id: workspaceId } }),
  previewRejectionEmail: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/rejection-email/preview`, data, { params: { workspace_id: workspaceId } }),
  sendRejectionEmails: (workspaceId, sessionId, data) => api.post(`/temp-sessions/${sessionId}/rejection-email/send`, data, { params: { workspace_id: workspaceId } }),
  sendOneRejectionEmail: (workspaceId, sessionId, candidateId, data) => api.post(`/temp-sessions/${sessionId}/rejection-email/send-one/${candidateId}`, data, { params: { workspace_id: workspaceId } }),
};

// Public temp session API (no auth)
export const tempSessionPublicAPI = {
  access: (token, password = null) => {
    const params = {};
    if (password) params.password = password;
    return fetch(`${API_BASE}/temp-sessions/public/${token}?${new URLSearchParams(params)}`).then(r => r.json());
  },
  verifyPassword: (token, password) => fetch(`${API_BASE}/temp-sessions/public/${token}/verify-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  }).then(r => r.json()),
  getContent: (token, itemId, password = null) => {
    const params = {};
    if (password) params.password = password;
    return fetch(`${API_BASE}/temp-sessions/public/${token}/content/${itemId}?${new URLSearchParams(params)}`).then(r => r.json());
  },
  confirmAttendance: (token, data) => fetch(`${API_BASE}/temp-sessions/public/${token}/attendance`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Erreur'); }); return r.json(); }),
  getAttendanceStatus: (token, name, email = null) => {
    const params = { candidate_name: name };
    if (email) params.candidate_email = email;
    return fetch(`${API_BASE}/temp-sessions/public/${token}/attendance/status?${new URLSearchParams(params)}`).then(r => r.json());
  },
  submitContact: (token, data) => fetch(`${API_BASE}/temp-sessions/public/${token}/contact`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Erreur'); }); return r.json(); }),
};

// VM Entries API
export const vmAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/vms`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/vms`, data),
  update: (workspaceId, vmId, data) => api.put(`/workspaces/${workspaceId}/vms/${vmId}`, data),
  delete: (workspaceId, vmId) => api.delete(`/workspaces/${workspaceId}/vms/${vmId}`),
};

// Recording Studio — SSH Connections API
export const sshAPI = {
  list: () => api.get('/studio/ssh-connections'),
  create: (data) => api.post('/studio/ssh-connections', data),
  update: (id, data) => api.put(`/studio/ssh-connections/${id}`, data),
  delete: (id) => api.delete(`/studio/ssh-connections/${id}`),
};

// Recording Studio — Settings API
export const studioSettingsAPI = {
  get: () => api.get('/studio/settings'),
  update: (data) => api.put('/studio/settings', data),
};

// Recording Studio — server-side WebM → MP4 conversion (ffmpeg)
export const studioConvertAPI = {
  start: (blob, filename, onUploadProgress) => {
    const form = new FormData();
    form.append('file', blob, filename);
    return api.post('/studio/convert-to-mp4', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress,
    });
  },
  status: (jobId) => api.get(`/studio/convert-to-mp4/${jobId}`),
  download: (jobId) => api.get(`/studio/convert-to-mp4/${jobId}/download`, {
    responseType: 'blob',
    timeout: 0,
  }),
};

// Recording Studio — render an editor timeline to MP4 (ffmpeg, async job)
export const studioRenderAPI = {
  start: (files, timeline, onUploadProgress) => {
    const form = new FormData();
    form.append('timeline', JSON.stringify(timeline));
    files.forEach((f) => form.append('files', f, f.name));
    return api.post('/studio/render', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress,
    });
  },
  status: (jobId) => api.get(`/studio/render/${jobId}`),
  download: (jobId) => api.get(`/studio/render/${jobId}/download`, {
    responseType: 'blob',
    timeout: 0,
  }),
};

// Recording Studio — Whisper transcription for auto subtitles (async job)
export const studioTranscribeAPI = {
  start: (file, language, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('language', language || 'fr');
    return api.post('/studio/transcribe', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress,
    });
  },
  status: (jobId) => api.get(`/studio/transcribe/${jobId}`),
};

// VM Columns API
export const vmColumnsAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/vm-columns`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/vm-columns`, data),
  update: (workspaceId, columnId, data) => api.put(`/workspaces/${workspaceId}/vm-columns/${columnId}`, data),
  delete: (workspaceId, columnId) => api.delete(`/workspaces/${workspaceId}/vm-columns/${columnId}`),
};

// Interview Module API
export const interviewAPI = {
  // Questions
  listQuestions: (workspaceId, params = {}) => api.get('/interviews/questions', { params: { workspace_id: workspaceId, ...params } }),
  createQuestion: (workspaceId, userId, data) => api.post('/interviews/questions', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  updateQuestion: (questionId, data) => api.put(`/interviews/questions/${questionId}`, data),
  deleteQuestion: (questionId) => api.delete(`/interviews/questions/${questionId}`),
  getDomains: (workspaceId) => api.get('/interviews/questions/domains', { params: { workspace_id: workspaceId } }),
  importQuestions: (workspaceId, userId, formData) => api.post('/interviews/questions/import', formData, { params: { workspace_id: workspaceId, user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } }),
  seedQuestions: (workspaceId, userId) => api.post('/interviews/questions/seed', null, { params: { workspace_id: workspaceId, user_id: userId } }),
  generateAIQuestions: (workspaceId, userId, params) => api.post('/interviews/questions/generate-ai', null, { params: { workspace_id: workspaceId, user_id: userId, ...params } }),
  generateAnswers: (workspaceId, params) => api.post('/interviews/questions/generate-answers', null, { params: { workspace_id: workspaceId, ...params } }),

  // Templates
  listTemplates: (workspaceId, params = {}) => api.get('/interviews/templates', { params: { workspace_id: workspaceId, ...params } }),
  createTemplate: (workspaceId, userId, data) => api.post('/interviews/templates', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  updateTemplate: (templateId, data) => api.put(`/interviews/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/interviews/templates/${templateId}`),
  duplicateTemplate: (templateId, workspaceId, userId) => api.post(`/interviews/templates/${templateId}/duplicate`, null, { params: { workspace_id: workspaceId, user_id: userId } }),

  // Candidates
  listCandidates: (workspaceId, params = {}) => api.get('/interviews/candidates', { params: { workspace_id: workspaceId, ...params } }),
  getCandidate: (candidateId) => api.get(`/interviews/candidates/${candidateId}`),
  createCandidate: (workspaceId, userId, data) => api.post('/interviews/candidates', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  createCandidateFromParticipant: (participantId, workspaceId, userId, targetRole = '') => api.post('/interviews/candidates/from-participant', null, { params: { participant_id: participantId, workspace_id: workspaceId, user_id: userId, target_role: targetRole } }),
  updateCandidate: (candidateId, data) => api.put(`/interviews/candidates/${candidateId}`, data),
  deleteCandidate: (candidateId) => api.delete(`/interviews/candidates/${candidateId}`),

  // Interviews (live)
  listInterviews: (workspaceId, params = {}) => api.get('/interviews', { params: { workspace_id: workspaceId, ...params } }),
  getInterview: (interviewId) => api.get(`/interviews/${interviewId}`),
  createInterview: (workspaceId, userId, data) => api.post('/interviews', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  updateInterview: (interviewId, data) => api.put(`/interviews/${interviewId}`, data),
  completeInterview: (interviewId) => api.post(`/interviews/${interviewId}/complete`),
  overrideRecommendation: (interviewId, data) => api.post(`/interviews/${interviewId}/override`, data),
  getInterviewPDF: (interviewId) => api.get(`/interviews/${interviewId}/pdf`),
};

// Notes Module API
export const notesAPI = {
  // Notes CRUD
  list: (workspaceId, userId, params = {}) => api.get('/notes/', { params: { workspace_id: workspaceId, user_id: userId, ...params } }),
  get: (noteId, userId) => api.get(`/notes/${noteId}`, { params: { user_id: userId } }),
  create: (workspaceId, userId, data) => api.post('/notes/', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  update: (noteId, userId, data, saveVersion = false) => api.put(`/notes/${noteId}`, data, { params: { user_id: userId, save_version: saveVersion } }),
  delete: (noteId, userId) => api.delete(`/notes/${noteId}`, { params: { user_id: userId } }),
  togglePin: (noteId, userId) => api.patch(`/notes/${noteId}/pin`, null, { params: { user_id: userId } }),
  // Folders
  listFolders: (workspaceId, userId) => api.get('/notes/folders', { params: { workspace_id: workspaceId, user_id: userId } }),
  createFolder: (workspaceId, userId, data) => api.post('/notes/folders', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  updateFolder: (folderId, userId, data) => api.put(`/notes/folders/${folderId}`, data, { params: { user_id: userId } }),
  deleteFolder: (folderId, userId) => api.delete(`/notes/folders/${folderId}`, { params: { user_id: userId } }),
  // Sharing
  share: (noteId, userId, data) => api.post(`/notes/${noteId}/share`, data, { params: { user_id: userId } }),
  unshare: (noteId, shareUserId, userId) => api.delete(`/notes/${noteId}/share/${shareUserId}`, { params: { user_id: userId } }),
  listShares: (noteId, userId) => api.get(`/notes/${noteId}/shares`, { params: { user_id: userId } }),
  listWorkspaceUsers: (workspaceId, userId) => api.get('/notes/users/workspace', { params: { workspace_id: workspaceId, user_id: userId } }),
  // Versions
  listVersions: (noteId, userId) => api.get(`/notes/${noteId}/versions`, { params: { user_id: userId } }),
  restoreVersion: (noteId, versionId, userId) => api.post(`/notes/${noteId}/versions/${versionId}/restore`, null, { params: { user_id: userId } }),
  // Tags & Templates
  listTags: (workspaceId, userId) => api.get('/notes/tags/all', { params: { workspace_id: workspaceId, user_id: userId } }),
  listTemplates: () => api.get('/notes/templates/all'),
  getTemplate: (templateId) => api.get(`/notes/templates/${templateId}`),
  // Image upload
  uploadImage: (workspaceId, userId, formData) => api.post('/notes/upload-image', formData, { params: { workspace_id: workspaceId, user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default api;

// Quick Notes Widget API (floating notepad for trainers/admins)
export const quicknotesAPI = {
  list: (workspaceId, userId, params = {}) => api.get('/quicknotes/', { params: { workspace_id: workspaceId, user_id: userId, ...params } }),
  create: (workspaceId, userId, data) => api.post('/quicknotes/', data, { params: { workspace_id: workspaceId, user_id: userId } }),
  update: (noteId, userId, data) => api.put(`/quicknotes/${noteId}`, data, { params: { user_id: userId } }),
  delete: (noteId, userId) => api.delete(`/quicknotes/${noteId}`, { params: { user_id: userId } }),
};

// Webinar / Live Classes API
export const webinarAPI = {
  // CRUD
  list: (params = {}) => api.get(`/webinars`, { params }),
  get: (webinarId) => api.get(`/webinars/${webinarId}`),
  create: (data) => api.post(`/webinars`, data),
  update: (webinarId, data) => api.put(`/webinars/${webinarId}`, data),
  delete: (webinarId) => api.delete(`/webinars/${webinarId}`),
  duplicate: (webinarId) => api.post(`/webinars/${webinarId}/duplicate`),
  updateStatus: (webinarId, status) => api.patch(`/webinars/${webinarId}/status?status=${status}`),

  // Live actions
  start: (webinarId) => api.post(`/webinars/${webinarId}/start`),
  end: (webinarId) => api.post(`/webinars/${webinarId}/end`),

  // Registrations
  register: (webinarId, data = {}) => api.post(`/webinars/${webinarId}/register`, data),
  listRegistrations: (webinarId, params = {}) => api.get(`/webinars/${webinarId}/registrations`, { params }),
  updateRegistration: (webinarId, regId, data) => api.patch(`/webinars/${webinarId}/registrations/${regId}`, data),

  // Messages
  listMessages: (webinarId, params = {}) => api.get(`/webinars/${webinarId}/messages`, { params }),

  // Polls
  createPoll: (webinarId, data) => api.post(`/webinars/${webinarId}/polls`, data),
  listPolls: (webinarId) => api.get(`/webinars/${webinarId}/polls`),
  updatePoll: (webinarId, pollId, data) => api.patch(`/webinars/${webinarId}/polls/${pollId}`, data),
  votePoll: (webinarId, pollId, data) => api.post(`/webinars/${webinarId}/polls/${pollId}/vote`, data),

  // Analytics
  getAnalytics: (webinarId) => api.get(`/webinars/${webinarId}/analytics`),
  getStats: () => api.get(`/webinar-stats`),
};

// Webinar Sessions (public registration module)
export const webinarSessionAPI = {
  // CRUD (admin)
  list: () => api.get('/webinar-sessions'),
  get: (id) => api.get(`/webinar-sessions/${id}`),
  create: (data) => api.post('/webinar-sessions', data),
  update: (id, data) => api.put(`/webinar-sessions/${id}`, data),
  delete: (id) => api.delete(`/webinar-sessions/${id}`),

  // Public
  getPublic: (slug) => axios.get(`${API_BASE}/public/webinar-session/${slug}`),
  registerPublic: (slug, data) => axios.post(`${API_BASE}/public/webinar-session/${slug}/register`, data),
  validateToken: (token) => axios.get(`${API_BASE}/public/webinar-join/${token}`),

  // Registrations (admin)
  listRegistrations: (sessionId) => api.get(`/webinar-sessions/${sessionId}/registrations`),
  approveRegistration: (sessionId, regId) => api.patch(`/webinar-sessions/${sessionId}/registrations/${regId}/approve`),
  rejectRegistration: (sessionId, regId) => api.patch(`/webinar-sessions/${sessionId}/registrations/${regId}/reject`),
  deleteRegistration: (sessionId, regId) => api.delete(`/webinar-sessions/${sessionId}/registrations/${regId}`),
  resendEmail: (sessionId, regId) => api.post(`/webinar-sessions/${sessionId}/registrations/${regId}/resend-email`),
};

// Showcase / Vitrine (boutique + portfolio)
export const showcaseAPI = {
  // Settings
  getSettings: () => api.get('/showcase/settings'),
  updateSettings: (data) => api.put('/showcase/settings', data),

  // Projects
  listProjects: () => api.get('/showcase/projects'),
  createProject: (data) => api.post('/showcase/projects', data),
  updateProject: (id, data) => api.put(`/showcase/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/showcase/projects/${id}`),

  // Experience
  listExperience: () => api.get('/showcase/experience'),
  createExperience: (data) => api.post('/showcase/experience', data),
  updateExperience: (id, data) => api.put(`/showcase/experience/${id}`, data),
  deleteExperience: (id) => api.delete(`/showcase/experience/${id}`),

  // Services
  listServices: () => api.get('/showcase/services'),
  createService: (data) => api.post('/showcase/services', data),
  updateService: (id, data) => api.put(`/showcase/services/${id}`, data),
  deleteService: (id) => api.delete(`/showcase/services/${id}`),

  // Testimonials
  listTestimonials: () => api.get('/showcase/testimonials'),
  createTestimonial: (data) => api.post('/showcase/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/showcase/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/showcase/testimonials/${id}`),

  // Formations (catalogue vitrine)
  listFormations: () => api.get('/showcase/formations'),
  createFormation: (data) => api.post('/showcase/formations', data),
  updateFormation: (id, data) => api.put(`/showcase/formations/${id}`, data),
  deleteFormation: (id) => api.delete(`/showcase/formations/${id}`),

  // IA — génération de contenu
  aiGenerate: (data) => api.post('/showcase/ai/generate', data),

  // Leads
  listLeads: () => api.get('/showcase/leads'),
  markLead: (id, handled = true) => api.patch(`/showcase/leads/${id}?handled=${handled}`),
  deleteLead: (id) => api.delete(`/showcase/leads/${id}`),

  // Public (no auth)
  getPublic: () => axios.get(`${API_BASE}/showcase/public`),
  submitContact: (data) => axios.post(`${API_BASE}/showcase/public/contact`, data),
};


// ==================== TUTORIALS API ====================

export const tutorialAPI = {
  // Pages CRUD
  list: (params = {}) => {
    const query = new URLSearchParams();
    if (params.user_id) query.append('user_id', params.user_id);
    if (params.workspace_id) query.append('workspace_id', params.workspace_id);
    if (params.course_id) query.append('course_id', params.course_id);
    if (params.status) query.append('status', params.status);
    if (params.search) query.append('search', params.search);
    const qs = query.toString();
    return api.get(`/tutorials/${qs ? '?' + qs : ''}`);
  },
  get: (pageId, userId) => api.get(`/tutorials/${pageId}?user_id=${userId}`),
  create: (data, userId) => api.post(`/tutorials/?user_id=${userId}`, data),
  update: (pageId, data, userId, saveVersion = false) =>
    api.put(`/tutorials/${pageId}?user_id=${userId}&save_version=${saveVersion}`, data),
  delete: (pageId, userId) => api.delete(`/tutorials/${pageId}?user_id=${userId}`),
  duplicate: (pageId, userId) => api.post(`/tutorials/${pageId}/duplicate?user_id=${userId}`),
  toggleStatus: (pageId, userId) => api.patch(`/tutorials/${pageId}/status?user_id=${userId}`),
  toggleVisibility: (pageId, userId) => api.patch(`/tutorials/${pageId}/visibility?user_id=${userId}`),
  reorder: (items, userId) => api.put(`/tutorials/reorder?user_id=${userId}`, { items }),
  // Versions
  listVersions: (pageId, userId) => api.get(`/tutorials/${pageId}/versions?user_id=${userId}`),
  restoreVersion: (pageId, versionId, userId) =>
    api.post(`/tutorials/${pageId}/versions/${versionId}/restore?user_id=${userId}`),
  // Upload
  uploadImage: (formData, userId) =>
    api.post(`/tutorials/upload-image?user_id=${userId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadVideo: (formData, userId) =>
    api.post(`/tutorials/upload-video?user_id=${userId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Export
  exportHTML: (pageId, userId) => api.get(`/tutorials/${pageId}/export/html?user_id=${userId}`, { responseType: 'blob' }),
  exportPDF: (pageId, userId) => api.get(`/tutorials/${pageId}/export/pdf?user_id=${userId}`, { responseType: 'blob' }),
  // Public access (no auth required)
  getPublic: (pageId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return fetch(`${backendUrl}/api/tutorials/public/${pageId}`).then(r => {
      if (!r.ok) throw new Error('Tutoriel non trouvé');
      return r.json();
    });
  },
  // Journal des tentatives de copie / capture sur les pages publiques (admin)
  getProtectionEvents: (userId, params = {}) => {
    const query = new URLSearchParams({ user_id: userId });
    if (params.pageId) query.append('page_id', params.pageId);
    if (params.eventType) query.append('event_type', params.eventType);
    if (params.sensibleOnly) query.append('sensible_only', 'true');
    if (params.limit) query.append('limit', String(params.limit));
    return api.get(`/tutorials/protection/events?${query.toString()}`);
  },
  // Link to course/workspace
  linkToCourse: (pageId, data, userId) =>
    api.put(`/tutorials/${pageId}?user_id=${userId}`, data),
  // AI
  aiGenerate: (data, userId) => api.post(`/tutorials/ai/generate?user_id=${userId}`, data),
  aiImprove: (data, userId) => api.post(`/tutorials/ai/improve?user_id=${userId}`, data),
  aiSummarize: (data, userId) => api.post(`/tutorials/ai/summarize?user_id=${userId}`, data),
  aiCodeExample: (data, userId) => api.post(`/tutorials/ai/code-example?user_id=${userId}`, data),
  aiHistory: (userId) => api.get(`/tutorials/ai/history?user_id=${userId}`),
};

